import React, { useState, useEffect, useCallback } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../ui/table";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { Badge } from "../ui/badge";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import {
  Loader2,
  Search,
  Calendar,
  Eye,
  RefreshCw,
  AlertTriangle,
  Ban,
  ArrowDownCircle,
  Package,
} from "lucide-react";
import api from "../../lib/api";
import toast from "react-hot-toast";
import Pagination from "../ui/pagination";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";
import { format } from "date-fns";
import OrderDetailsModal from "./OrderDetailsModal";

// Bucket definitions for refund processing
type RefundBucket = "refund_pending" | "payment_pending" | "cancelled" | "delivered_dispatched" | "all";

interface BucketConfig {
  key: RefundBucket;
  label: string;
  description: string;
  icon: React.ReactNode;
  color: string;
  // Statuses to fetch from API
  statuses: string[];
}

const REFUND_BUCKETS: BucketConfig[] = [
  {
    key: "refund_pending",
    label: "Refund Pending",
    description: "Orders where customer is owed a refund due to item rejections or price changes",
    icon: <ArrowDownCircle className="w-5 h-5 text-orange-500" />,
    color: "border-orange-200 bg-orange-50 dark:border-orange-800 dark:bg-orange-950",
    statuses: ["CONFIRMED", "PACKED", "OUT_FOR_DELIVERY", "DELIVERED"],
  },
  {
    key: "payment_pending",
    label: "Payment Pending",
    description: "Orders where additional payment is due from customer",
    icon: <AlertTriangle className="w-5 h-5 text-amber-500" />,
    color: "border-amber-200 bg-amber-50 dark:border-amber-800 dark:bg-amber-950",
    statuses: ["CONFIRMED", "PACKED", "OUT_FOR_DELIVERY", "DELIVERED"],
  },
  {
    key: "cancelled",
    label: "Cancelled Orders",
    description: "Cancelled orders requiring full refund processing",
    icon: <Ban className="w-5 h-5 text-red-500" />,
    color: "border-red-200 bg-red-50 dark:border-red-800 dark:bg-red-950",
    statuses: ["CANCELLED", "PRESCRIPTION_REJECTED"],
  },
  {
    key: "delivered_dispatched",
    label: "Delivered / Dispatched",
    description: "Delivered or dispatched orders eligible for partial item-level refunds",
    icon: <Package className="w-5 h-5 text-blue-500" />,
    color: "border-blue-200 bg-blue-50 dark:border-blue-800 dark:bg-blue-950",
    statuses: ["DELIVERED", "DISPATCHED"],
  },
];

interface RefundOrder {
  tracking_id: string;
  customer_name: string;
  customer_phone: string;
  created_at: string;
  total: number;
  status: string;
  payment_status: string;
  payment_method: string;
  charges_metadata?: {
    amountPaid?: number;
    adjustments?: {
      source: string;
      billedTotal: number;
      amountPaid: number;
      refundDue: number;
      additionalDue: number;
      items: Array<{
        itemName: string;
        type: string;
        originalPrice: number;
        adjustedPrice: number;
      }>;
    };
  };
}

const RefundProcessing = () => {
  const [orders, setOrders] = useState<RefundOrder[]>([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [search, setSearch] = useState("");
  const [limit, setLimit] = useState("20");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [selectedOrderId, setSelectedOrderId] = useState<string | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [activeBucket, setActiveBucket] = useState<RefundBucket>("refund_pending");
  const [bucketCounts, setBucketCounts] = useState<Record<RefundBucket, number>>({
    refund_pending: 0,
    payment_pending: 0,
    cancelled: 0,
    delivered_dispatched: 0,
    all: 0,
  });

  const getActiveBucketConfig = useCallback(() => {
    return REFUND_BUCKETS.find((b) => b.key === activeBucket) || REFUND_BUCKETS[0];
  }, [activeBucket]);

  const fetchOrders = useCallback(async () => {
    setLoading(true);
    try {
      const bucketConfig = getActiveBucketConfig();
      // For refund_pending/payment_pending, the JSONB filter is the primary filter
      // so don't restrict by statuses (refund_pending can include cancelled orders too)
      const useRefundBucket = activeBucket === "refund_pending" || activeBucket === "payment_pending";
      const params = new URLSearchParams({
        page: page.toString(),
        limit: limit,
        ...(search && { search }),
        ...(startDate && { startDate }),
        ...(endDate && { endDate }),
        // delivered_dispatched bucket uses status filter directly
        ...(!useRefundBucket && { statuses: bucketConfig.statuses.join(",") }),
        includeMetadata: "true",
        ...(useRefundBucket && { refundBucket: activeBucket }),
      });

      const response = await api.getB2COrders(params);
      const responseData = response.data;

      if (response.status === "success") {
        const ordersData = responseData.orders || responseData || [];
        const paginationData =
          responseData.data?.pagination || responseData.pagination || {};

        setOrders(ordersData);
        setTotalPages(paginationData.totalPages || 1);
      } else {
        toast.error("Failed to fetch orders");
      }
    } catch (error) {
      console.error("Error fetching refund orders:", error);
      toast.error("Error fetching orders");
    } finally {
      setLoading(false);
    }
  }, [page, limit, search, startDate, endDate, activeBucket, getActiveBucketConfig]);

  const fetchBucketCounts = useCallback(async () => {
    try {
      const params = new URLSearchParams({
        ...(search && { search }),
        ...(startDate && { startDate }),
        ...(endDate && { endDate }),
      });

      const response = await api.getGroupedB2COrders(params);

      if (response.status === "success") {
        const groups: { status: string; count: number }[] = response.data || [];

        // Count cancelled orders
        const cancelledCount = groups
          .filter((g) => ["CANCELLED", "PRESCRIPTION_REJECTED"].includes(g.status))
          .reduce((sum, g) => sum + Number(g.count), 0);

        // Count delivered/dispatched orders
        const deliveredDispatchedCount = groups
          .filter((g) => ["DELIVERED", "DISPATCHED"].includes(g.status))
          .reduce((sum, g) => sum + Number(g.count), 0);

        // For refund/payment pending, we rely on the order list counts
        // since they require charges_metadata inspection
        setBucketCounts((prev) => ({
          ...prev,
          cancelled: cancelledCount,
          delivered_dispatched: deliveredDispatchedCount,
        }));
      }
    } catch (error) {
      console.error("Error fetching bucket counts:", error);
    }
  }, [search, startDate, endDate]);

  useEffect(() => {
    fetchOrders();
  }, [fetchOrders]);

  useEffect(() => {
    fetchBucketCounts();
  }, [fetchBucketCounts]);

  const handleSearch = () => {
    setPage(1);
    fetchOrders();
    fetchBucketCounts();
  };

  const handleViewOrder = (orderId: string) => {
    setSelectedOrderId(orderId);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedOrderId(null);
  };

  const formatDate = (dateString: string) => {
    try {
      return format(new Date(dateString), "dd MMM yyyy, HH:mm");
    } catch {
      return dateString;
    }
  };

  const getRefundInfo = (order: RefundOrder) => {
    const adj = order.charges_metadata?.adjustments;
    if (!adj) {
      // Cancelled order with no adjustments — full refund of amountPaid
      if (["CANCELLED", "PRESCRIPTION_REJECTED"].includes(order.status)) {
        const amountPaid = Number(order.charges_metadata?.amountPaid ?? order.total) || 0;
        return { refundDue: amountPaid, additionalDue: 0, source: "cancellation" };
      }
      return { refundDue: 0, additionalDue: 0, source: "-" };
    }
    return {
      refundDue: Number(adj.refundDue) || 0,
      additionalDue: Number(adj.additionalDue) || 0,
      source: adj.source,
    };
  };

  const getStatusBadgeVariant = (status: string) => {
    switch (status.toUpperCase()) {
      case "CANCELLED":
      case "PRESCRIPTION_REJECTED":
        return "destructive";
      case "DELIVERED":
        return "default";
      default:
        return "secondary";
    }
  };

  return (
    <div>
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <RefreshCw className="w-5 h-5" />
            Refund Processing
          </CardTitle>

          {/* Bucket Cards */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mt-4">
            {REFUND_BUCKETS.map((bucket) => (
              <Card
                key={bucket.key}
                className={`p-4 cursor-pointer transition-all ${bucket.color} ${
                  activeBucket === bucket.key
                    ? "ring-2 ring-indigo-500 dark:ring-indigo-400"
                    : "hover:shadow-md"
                }`}
                onClick={() => {
                  setActiveBucket(bucket.key as RefundBucket);
                  setPage(1);
                }}
              >
                <div className="flex items-center gap-3">
                  {bucket.icon}
                  <div>
                    <h3 className="font-semibold text-sm">{bucket.label}</h3>
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                      {bucket.description}
                    </p>
                  </div>
                </div>
                {bucket.key === "cancelled" && bucketCounts.cancelled > 0 && (
                  <p className="text-2xl font-bold mt-2">{bucketCounts.cancelled}</p>
                )}
                {bucket.key === "delivered_dispatched" && bucketCounts.delivered_dispatched > 0 && (
                  <p className="text-2xl font-bold mt-2">{bucketCounts.delivered_dispatched}</p>
                )}
              </Card>
            ))}
          </div>
        </CardHeader>

        <CardContent>
          <div className="space-y-4">
            {/* Filters */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                <Input
                  placeholder="Search by order ID, customer..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  onKeyPress={(e) => e.key === "Enter" && handleSearch()}
                  className="pl-10"
                />
              </div>

              <div className="relative">
                <Calendar className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                <Input
                  type="date"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                  className="pl-10"
                />
              </div>

              <div className="relative">
                <Calendar className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                <Input
                  type="date"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                  className="pl-10"
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Select value={limit} onValueChange={setLimit}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="10">10 per page</SelectItem>
                  <SelectItem value="20">20 per page</SelectItem>
                  <SelectItem value="50">50 per page</SelectItem>
                  <SelectItem value="100">100 per page</SelectItem>
                </SelectContent>
              </Select>

              <div className="flex justify-end">
                <Button onClick={handleSearch} disabled={loading}>
                  <Search className="w-4 h-4 mr-2" />
                  Search
                </Button>
              </div>
            </div>
          </div>

          {/* Orders Table */}
          {loading ? (
            <div className="flex justify-center items-center h-64 mt-8">
              <Loader2 className="h-8 w-8 animate-spin" />
            </div>
          ) : (
            <>
              <div className="overflow-x-auto mt-6">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Order Number</TableHead>
                      <TableHead>Customer</TableHead>
                      <TableHead>Phone</TableHead>
                      <TableHead>Order Date</TableHead>
                      <TableHead>Order Total</TableHead>
                      <TableHead>Amount Paid</TableHead>
                      <TableHead>Status</TableHead>
                      {activeBucket === "refund_pending" || activeBucket === "cancelled" || activeBucket === "delivered_dispatched" ? (
                        <TableHead className="text-red-600">Refund Due</TableHead>
                      ) : (
                        <TableHead className="text-amber-600">Additional Due</TableHead>
                      )}
                      <TableHead>Source</TableHead>
                      <TableHead>Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {orders.map((order) => {
                      const refundInfo = getRefundInfo(order);
                      return (
                        <TableRow key={order.tracking_id}>
                          <TableCell className="font-medium">
                            {order.tracking_id}
                          </TableCell>
                          <TableCell>{order.customer_name}</TableCell>
                          <TableCell>{order.customer_phone}</TableCell>
                          <TableCell>{formatDate(order.created_at)}</TableCell>
                          <TableCell>
                            {(parseFloat(String(order.total)) || 0).toFixed(2)}
                          </TableCell>
                          <TableCell className="font-medium">
                            {Number(order.charges_metadata?.amountPaid ?? order.total).toFixed(2)}
                          </TableCell>
                          <TableCell>
                            <Badge variant={getStatusBadgeVariant(order.status) as any}>
                              {order.status}
                            </Badge>
                          </TableCell>
                          <TableCell>
                            {activeBucket === "refund_pending" || activeBucket === "cancelled" || activeBucket === "delivered_dispatched" ? (
                              refundInfo.refundDue > 0 ? (
                                <span className="font-semibold text-red-600">
                                  {refundInfo.refundDue.toFixed(2)}
                                </span>
                              ) : (
                                <span className="text-gray-400">-</span>
                              )
                            ) : refundInfo.additionalDue > 0 ? (
                              <span className="font-semibold text-amber-600">
                                {refundInfo.additionalDue.toFixed(2)}
                              </span>
                            ) : (
                              <span className="text-gray-400">-</span>
                            )}
                          </TableCell>
                          <TableCell>
                            <Badge variant="outline" className="text-xs">
                              {refundInfo.source}
                            </Badge>
                          </TableCell>
                          <TableCell>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleViewOrder(order.tracking_id)}
                            >
                              <Eye className="w-4 h-4 mr-1" />
                              View
                            </Button>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </div>

              {orders.length === 0 && (
                <div className="text-center py-8 text-gray-500">
                  No orders found in this category
                </div>
              )}

              {totalPages > 1 && (
                <div className="flex justify-center mt-4">
                  <Pagination
                    page={page}
                    totalPages={totalPages}
                    onPageChange={setPage}
                  />
                </div>
              )}
            </>
          )}
        </CardContent>
      </Card>

      {/* Order Details Modal */}
      {selectedOrderId && (
        <OrderDetailsModal
          orderId={selectedOrderId}
          isOpen={isModalOpen}
          onClose={handleCloseModal}
          variant="refund"
        />
      )}
    </div>
  );
};

export default RefundProcessing;
